import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PrestadorServico } from '@/types/ordemServico';
import { captureError } from '@/lib/sentry';
import { useOrganization } from '@/hooks/useOrganization';
import {
  listPrestadores,
  getMeuCadastroPrestador,
  getPrestadorById,
  criarPrestador,
  atualizarPrestador,
} from '@/services/prestadores.service';

export function usePrestadores() {
  const { organizationId } = useOrganization();

  return useQuery<PrestadorServico[]>({
    queryKey: ['prestadores', organizationId],
    queryFn: () => listPrestadores(),
  });
}

export function useMeuCadastroPrestador() {
  const { organizationId } = useOrganization();

  return useQuery<PrestadorServico | null>({
    queryKey: ['meu-cadastro-prestador', organizationId],
    queryFn: () => getMeuCadastroPrestador(),
  });
}

export function usePrestador(id: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery<PrestadorServico | null>({
    queryKey: ['prestador', organizationId, id],
    queryFn: () => (id ? getPrestadorById(id) : null),
    enabled: !!id,
  });
}

export function useCriarPrestador() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (dados: {
      profile_id: string;
      telefone?: string;
      servicos_oferecidos?: string[];
      observacoes?: string;
    }) => criarPrestador({ ...dados, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prestadores'] });
      toast.success(t('team.toast.providerCreated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useCriarPrestador' } });
      console.error('Erro ao criar prestador:', error);
      toast.error(t('team.toast.providerCreateError'));
    },
  });
}

export function useAtualizarPrestador() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id,
      dados,
    }: {
      id: string;
      dados: Partial<{
        telefone: string;
        servicos_oferecidos: string[];
        observacoes: string;
      }>;
    }) => atualizarPrestador({ id, dados }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prestadores'] });
      queryClient.invalidateQueries({ queryKey: ['prestador'] });
      queryClient.invalidateQueries({ queryKey: ['meu-cadastro-prestador'] });
      toast.success(t('team.toast.providerUpdated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAtualizarPrestador' } });
      console.error('Erro ao atualizar prestador:', error);
      toast.error(t('team.toast.providerUpdateError'));
    },
  });
}

